import React, {
	type FC,
	type ComponentType,
	useCallback,
	useMemo,
	useEffect,
	useState,
	memo,
} from 'react';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { di } from 'react-magnetic-di';
import type { RequestCreateFormState } from 'state/persisted/request-create';
import { useIssueAdjustmentsActions } from '@atlassian/ui-modifications-core/controllers/issue-adjustments/main';
import { useAdjustmentsEnabled } from '@atlassian/ui-modifications-core/controllers/adjustments-context';
import { useFieldsOverrides } from './use-fields-overrides';
import { useInitializeFields } from './use-initalize-fields';
import { useInitializeNoScreenTab } from './use-initialize-tabs';
import type { RequestCreateFormWithHOCsProps } from 'view/request-create-form/types';

export type PropsToOverride = Pick<
	RequestCreateFormWithHOCsProps,
	'formData' | 'onFieldChangedByUser' | 'onFieldBlurredByUser'
>;
export interface Props {
	formData: RequestCreateFormState;
	setOverrideProps: (props: PropsToOverride) => void;
}

export const EnabledUiModificationsDecorator = memo(({ formData, setOverrideProps }: Props) => {
	di(useInitializeFields, useInitializeNoScreenTab, useFieldsOverrides, useIssueAdjustmentsActions);

	useInitializeFields({
		fields: formData.fields,
		urlParamFieldValueOverrides: formData.urlParamFieldValueOverrides,
	});
	useInitializeNoScreenTab();
	const { fields } = useFieldsOverrides({ fields: formData.fields });
	const { clearAppliedValue, processChange } = useIssueAdjustmentsActions();

	const formDataWithOverrides = useMemo(() => {
		return {
			...formData,
			fields,
		};
	}, [formData, fields]);

	const onFieldChangedByUser = useCallback(
		({
			field,
			additionalFieldData,
		}: {
			field: string;
			value: unknown;
			notFromUserInteraction?: boolean;
			additionalFieldData?: {
				rawObject: object;
				rawValue?: unknown;
			};
		}) => {
			processChange({
				fieldId: field,
				fieldValue: additionalFieldData?.rawValue,
				eventType: 'change',
			});
			// Once we have applied the value, we clear it from the state, so we don't apply it again
			// in the future and overwrite whatever the user has changed between now and then
			clearAppliedValue(field);
		},
		[clearAppliedValue, processChange],
	);

	const onFieldBlurredByUser = useCallback(
		({
			field,
			additionalFieldData,
		}: {
			field: string;
			value: unknown;
			additionalFieldData: { rawObject: object; rawValue: unknown };
		}): void => {
			processChange({
				fieldId: field,
				fieldValue: additionalFieldData?.rawValue,
				eventType: 'blur',
			});
			// Once we have applied the value, we clear it from the state, so we don't apply it again
			// in the future and overwrite whatever the user has changed between now and then
			clearAppliedValue(field);
		},
		[clearAppliedValue, processChange],
	);

	useEffect(() => {
		setOverrideProps({
			onFieldChangedByUser,
			onFieldBlurredByUser,
			formData: formDataWithOverrides,
		});
	}, [formDataWithOverrides, setOverrideProps, onFieldChangedByUser, onFieldBlurredByUser]);

	return null;
});

EnabledUiModificationsDecorator.displayName = 'EnabledUiModificationsDecorator';

export const FormDecorator: FC<
	RequestCreateFormWithHOCsProps & {
		RequestCreateForm: ComponentType<RequestCreateFormWithHOCsProps>;
	}
> = (props) => {
	di(useAdjustmentsEnabled, EnabledUiModificationsDecorator);
	const [overrideProps, setOverrideProps] = useState<PropsToOverride>({});
	const isUiModificationsEnabled = useAdjustmentsEnabled();

	const { RequestCreateForm, ...requestCreateFormProps } = props;
	const { formData, portalId, requestTypeId, isPrefillPending } = requestCreateFormProps;

	// Do not mount EnabledUiModificationsDecorator while URL-param pre-fill is still being
	// resolved by the epic (isPrefillPending === true). useInitializeFields fires its effect
	// only once (on mount), so if it mounts before hydratedUrlParamOverrides arrives from the
	// epic, it will initialise UIM with an empty urlParamFieldValueOverrides and getValue()
	// will return null in onInit even though the field visually shows the pre-selected value.
	// Delaying the mount until isPrefillPending is false ensures the correct values are
	// available on the first (and only) call to initializeFields.
	const isReadyToMount = !fg('jsm_uim_queryparam_prefill_fix') || !isPrefillPending;

	return (
		<>
			<RequestCreateForm {...requestCreateFormProps} {...overrideProps} />
			{isUiModificationsEnabled && formData && portalId && requestTypeId && isReadyToMount && (
				<EnabledUiModificationsDecorator setOverrideProps={setOverrideProps} formData={formData} />
			)}
		</>
	);
};

/**
 * Built to wrap RequestCreateFrom with UI modifications overrides.
 * It's responsible for initialazing UIM data as well as overrides (without value).
 */
export const withUiModificationsFormDecorator = () => {
	return (RequestCreateForm: ComponentType<RequestCreateFormWithHOCsProps>) => {
		const Decorator = (props: RequestCreateFormWithHOCsProps) => (
			<FormDecorator RequestCreateForm={RequestCreateForm} {...props} />
		);
		Decorator.displayName = `withUiModificationsFormDecorator(${
			RequestCreateForm.displayName || RequestCreateForm.name
		})`;
		return Decorator;
	};
};
